import { useEffect, useRef, useState } from "react";
import { Emblem } from "@/components/Emblem";

// DR P8 Jazz – dansk live-stream, døgnet rundt, ingen login, ingen cookies.
// Spilles direkte i et <audio>-element uden SDK.
type Stream = { url: string; title: string; kicker: string };
const DAILY: Stream = {
  url: "https://live-icy.dr.dk/A/A22H.mp3",
  title: "DR P8 Jazz",
  kicker: "Dagens stream",
};


const CLOSED_KEY = "flaneriet.player.closed";

export function FloatingPlayer() {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(CLOSED_KEY) === "1") setClosed(true);
    } catch {}
  }, []);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPlaying = () => {
      setPlaying(true);
      setLoading(false);
    };
    const onPause = () => setPlaying(false);
    const onWaiting = () => setLoading(true);
    const onError = () => {
      setLoading(false);
      setPlaying(false);
    };
    audio.addEventListener("playing", onPlaying);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("error", onError);
    return () => {
      audio.removeEventListener("playing", onPlaying);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("error", onError);
    };
  }, []);

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      return;
    }
    // Streams har ingen begyndelse – spol altid til "nu" ved genstart, så vi
    // ikke afspiller gammel buffer.
    try {
      setLoading(true);
      audio.load();
      await audio.play();
    } catch {
      setLoading(false);
    }
  };

  const handleClose = () => {
    const audio = audioRef.current;
    if (audio && !audio.paused) audio.pause();
    try {
      sessionStorage.setItem(CLOSED_KEY, "1");
    } catch {}
    setClosed(true);
    setExpanded(false);
  };

  // Selve audio-elementet – usynligt, uden kontroller, preload="none" så
  // ingen bytes hentes før brugeren klikker. Ligger uden for de betingede
  // returns så refen overlever åbn/luk-cyklusser.
  const audioEl = (
    <audio
      ref={audioRef}
      src={DAILY.url}
      preload="none"
      crossOrigin="anonymous"
      aria-hidden
      style={{ display: "none" }}
    />
  );

  // Lukket tilstand – helt væk resten af sessionen
  if (closed) return null;


  const kicker = playing ? "Nu spiller" : DAILY.kicker;
  const source = DAILY.title;
  const title = "Dagens plade";

  // Play/pause-mærke – trekant eller to bjælker, tegnet i cirkelrammen.
  const PlayGlyph = ({ size = 44, color = "#d8a892" }: { size?: number; color?: string }) => (
    <span
      className="inline-flex items-center justify-center rounded-full"
      style={{ width: size, height: size, border: `1px solid ${color}` }}
    >
      {playing ? (
        <span className="inline-flex items-center" style={{ gap: size >= 60 ? 6 : 4 }}>
          <span
            style={{
              display: "block",
              width: size >= 60 ? 5 : size <= 42 ? 3 : 4,
              height: size >= 60 ? 22 : size <= 42 ? 13 : 15,
              background: color,
            }}
          />
          <span
            style={{
              display: "block",
              width: size >= 60 ? 5 : size <= 42 ? 3 : 4,
              height: size >= 60 ? 22 : size <= 42 ? 13 : 15,
              background: color,
            }}
          />
        </span>
      ) : (
        <span
          style={{
            width: 0,
            height: 0,
            borderStyle: "solid",
            borderWidth:
              size >= 60
                ? "10px 0 10px 16px"
                : size <= 42
                  ? "6px 0 6px 10px"
                  : "7px 0 7px 11px",
            borderColor: `transparent transparent transparent ${color}`,
            marginLeft: size >= 60 ? 4 : 2,
          }}
        />
      )}
    </span>
  );

  return (
    <>
      {audioEl}
      {/* Udvidet lytte-tilstand – papir-kort */}
      {expanded && (
        <div
          className="fixed inset-0 z-50 print:hidden flex items-end md:items-center justify-center bg-ink/40 backdrop-blur-[2px] animate-[fadeIn_.2s_ease]"
          onClick={() => setExpanded(false)}
          role="presentation"
        >
          <div
            role="dialog"
            aria-label="Pladespilleren"
            onClick={(e) => e.stopPropagation()}
            className="relative w-full md:w-[560px] max-w-full bg-paper border border-ink shadow-[0_40px_90px_-50px_rgba(26,26,23,0.5)]"
          >
            <div className="flex justify-between items-start px-7 py-6 border-b border-rule">
              <div>
                <div className="font-serif font-medium text-[11px] tracking-[0.2em] uppercase text-accent">
                  {kicker} · Musik
                </div>
                <div className="font-serif italic text-[14px] text-ink-soft mt-1.5">
                  et stille øjeblik
                </div>
              </div>
              <button
                type="button"
                aria-label="Minimér"
                onClick={() => setExpanded(false)}
                className="p-1.5 hover:opacity-70 transition-opacity"
              >
                <span
                  className="inline-block w-[11px] h-[11px]"
                  style={{
                    borderBottom: "1.5px solid #55524a",
                    borderRight: "1.5px solid #55524a",
                    transform: "rotate(45deg)",
                  }}
                />
              </button>
            </div>

            <div className="px-7 pt-8 pb-7 text-center">
              <h2 className="font-serif font-normal text-[34px] md:text-[38px] leading-[1.08] m-0 mb-1.5 text-ink">
                {title}
              </h2>
              <div className="font-serif italic text-[16px] text-ink-soft mb-8">
                {source}
              </div>

              {/* Én stor play/pause – ingen scrubber (streams har ingen slut) */}
              <div className="flex items-center justify-center">
                <button
                  type="button"
                  aria-label={playing ? "Pause" : "Afspil"}
                  aria-pressed={playing}
                  onClick={togglePlayback}
                  disabled={loading}
                  className="bg-transparent border-0 cursor-pointer p-0 disabled:opacity-60 disabled:cursor-wait"
                >
                  <span
                    className="inline-flex items-center justify-center rounded-full"
                    style={{ width: 66, height: 66, border: "1px solid #1a1a17" }}
                  >
                    {playing ? (
                      <span className="inline-flex items-center gap-[7px]">
                        <span style={{ display: "block", width: 5, height: 24, background: "#1a1a17" }} />
                        <span style={{ display: "block", width: 5, height: 24, background: "#1a1a17" }} />
                      </span>
                    ) : (
                      <span
                        style={{
                          width: 0,
                          height: 0,
                          borderStyle: "solid",
                          borderWidth: "10px 0 10px 16px",
                          borderColor: "transparent transparent transparent #1a1a17",
                          marginLeft: 4,
                        }}
                      />
                    )}
                  </span>
                </button>
              </div>

              <p className="font-serif italic text-[13px] text-ink-soft mt-7 max-w-[380px] mx-auto">
                Uafbrudt strøm – sæt på, hæld et glas, sid ned.
              </p>
            </div>

            <div className="border-t border-rule px-7 py-4 flex justify-between items-center">
              <span className="inline-flex items-center gap-2 bg-transparent border border-ink rounded-full font-serif text-[13px] px-3.5 py-1 text-ink">
                <span className="on-air-dot" aria-hidden />
                {playing ? "On air" : "Live"}
              </span>
              <span className="font-serif text-[11px] tracking-[0.14em] uppercase text-ink-soft">
                Langsomt liv · lyt
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Mini-bar (vedvarende) */}
      <div className="floating-player fixed z-40 print:hidden bottom-0 right-0 left-0">
        <div className="relative bg-ink text-paper shadow-[0_-20px_50px_-30px_rgba(26,26,23,0.5)] overflow-hidden">
          <div className="content-canvas flex items-center gap-4 md:gap-5 py-3 md:py-4">
            <button
              type="button"
              onClick={togglePlayback}
              disabled={loading}
              aria-pressed={playing}
              aria-label={playing ? "Pause" : "Afspil"}
              className="bg-transparent border-0 cursor-pointer flex-none p-0 disabled:opacity-60 disabled:cursor-wait min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <PlayGlyph size={44} color="#d8a892" />
            </button>

            <div className="min-w-0 flex-1">
              <div className="font-serif font-medium text-[10px] md:text-[11px] tracking-[0.18em] md:tracking-[0.2em] uppercase text-accent-onDark mb-0.5 md:mb-1 flex items-center gap-2">
                {playing && <span className="on-air-dot" aria-hidden />}
                {kicker}
              </div>
              <div className="font-serif text-[15px] md:text-[18px] text-paper whitespace-nowrap overflow-hidden text-ellipsis">
                {title} – <span className="italic text-[#e7d8cd]">{source}</span>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-5 flex-none">
              <span
                aria-label={playing ? "Streamer live" : "Klar til at streame"}
                className="bg-transparent border border-paper/30 rounded-full text-[#e7d8cd] font-serif text-[13px] px-3 py-1 inline-flex items-center gap-2"
              >
                <span className="on-air-dot" aria-hidden />
                {playing ? "On air" : "Live"}
              </span>
              <button
                type="button"
                aria-label="Udvid afspiller"
                onClick={() => setExpanded(true)}
                className="bg-transparent border-0 cursor-pointer p-1.5 hover:opacity-70 transition-opacity min-h-[36px] min-w-[36px] flex items-center justify-center"
              >
                <span
                  className="inline-block w-[9px] h-[9px]"
                  style={{
                    borderTop: "1.5px solid #d8a892",
                    borderRight: "1.5px solid #d8a892",
                    transform: "rotate(-45deg)",
                  }}
                />
              </button>
            </div>

            <button
              type="button"
              aria-label="Luk afspiller"
              onClick={handleClose}
              className="bg-transparent border-0 text-[#8f857a] hover:text-paper font-serif text-[22px] md:text-[24px] leading-none cursor-pointer px-1 flex-none min-h-[44px] min-w-[36px] transition-colors"
            >
              ×
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
